import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getPersonScheduleApi, getPlannerApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { currentUser, CW, ErrorBox, Eyebrow, InfoHint, Loading, PageTitle, Panel, Pill, TextLink } from "./cwUi";
import ScheduleChangeDialog from "./ScheduleChangeDialog";
import PlanSkipDialog from "./PlanSkipDialog";

/**
 * P3.7 — weekly check-in planner (design PDF page 5, "Spread the week",
 * same layout: filter bar, one card per day, "Clients in this programme").
 * NEW path /coach/check-ins/planner. No old screen did this; closest is
 * the journey's People tab (/trackers/:id → People). That tab stays.
 */

export default function WeeklyPlannerPage() {
  const navigate = useNavigate();
  const [trackerId, setTrackerId] = useState("");
  const [week, setWeek] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [dayFilter, setDayFilter] = useState("");
  const [editing, setEditing] = useState(null); // { row, mode: "day" | "plan", schedule }
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const result = await getPlannerApi({ trackerId, week });
    if (result?.code === 200) {
      setData(result);
      if (!trackerId && result.tracker?.id) setTrackerId(result.tracker.id);
    } else setError(result?.message || "Could not load the planner");
    setLoading(false);
  }, [trackerId, week]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = data?.rows || [];
    if (dayFilter) list = list.filter((r) => r.opensThisWeek === dayFilter);
    if (q) list = list.filter((r) => String(r.name || "").toLowerCase().includes(q));
    return list;
  }, [data, dayFilter, search]);


  const openEditor = async (row, mode) => {
    const result = await getPersonScheduleApi(data.tracker.id, { personKey: row.personKey, memberId: row.memberId });
    if (result?.code === 200) {
      setEditing({
        row,
        mode,
        schedule: {
          current: result.current,
          rules: result.rules,
          upcoming: result.upcoming,
          reviewPlan: result.reviewPlan,
        },
        personKey: result.person?.personKey || row.personKey,
      });
    }
  };

  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorBox message={error} onRetry={load} />;

  const rules = data?.rules || {};
  const me = currentUser();
  const coachName = `${me.first_name || ""} ${me.last_name || ""}`.trim();
  const COLS = "minmax(0,1.2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 230px";
  const canChangeDay = rules.allowCheckInDayChange && rules.dayChangeSupported !== false;
  const changedRow = (data?.rows || []).find((r) => r.changedFrom);

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 1320, mx: "auto", pb: 4 }}>
        <Eyebrow sx={{ color: CW.primary, fontSize: 12.5, mb: 0.5 }}>Check-ins / Weekly planner</Eyebrow>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box>
            <PageTitle>
              Spread the week
              <InfoHint id="planner.header" />
            </PageTitle>
            <Typography sx={{ fontSize: { xs: 14, md: 17 }, color: CW.muted, mt: 0.5 }}>
              One programme. Individual check-in days for each client.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <button type="button" className="small-contained-outlined-button" style={{ padding: "10px 20px", color: CW.ink, borderColor: CW.line }} onClick={() => navigate("/coach/today")}>
              Today
            </button>
            {data?.tracker?.id ? (
              <button
                type="button"
                className="small-contained-outlined-button"
                style={{ padding: "10px 24px", minWidth: 200, color: CW.ink, borderColor: CW.line }}
                onClick={() => navigate(`/coach/check-ins/${data.tracker.id}/questions`)}
                title="Check-in questions for this journey"
              >
                Tracker settings
              </button>
            ) : null}
          </Box>
        </Box>

        {/* Filter bar */}
        <Panel info="planner.filters" sx={{ mb: 3, py: { xs: 1.5, md: 1.5 } }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.5, md: 4 }, alignItems: "center" }}>
            {coachName ? <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>{coachName}</Typography> : null}
            <TextField
              select
              size="small"
              variant="standard"
              value={data?.tracker?.id || ""}
              onChange={(e) => {
                setTrackerId(e.target.value);
                setDayFilter("");
              }}
              InputProps={{ disableUnderline: true }}
              inputProps={{ "aria-label": "Journey" }}
              sx={{ minWidth: 200, "& .MuiSelect-select": { fontSize: 15, fontWeight: 700, color: CW.ink } }}
            >
              {(data?.trackers || []).map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            {data?.tracker ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                <IconButton size="small" onClick={() => setWeek(data.week.prev)} aria-label="Previous week">
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: CW.ink }}>{data.week?.label}</Typography>
                <IconButton size="small" onClick={() => setWeek(data.week.next)} aria-label="Next week">
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
                <TextLink onClick={() => setWeek("")} sx={{ fontSize: 12.5, ml: 0.5 }}>
                  This week
                </TextLink>
              </Box>
            ) : null}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              {rules.cadenceLabel ? <Typography sx={{ fontSize: 13.5, color: CW.muted }}>{rules.cadenceLabel}</Typography> : null}
              {rules.checkInTime ? (
                <Typography sx={{ fontSize: 13.5, color: CW.muted }}>
                  · {rules.checkInTime} {rules.checkInTimeZone || ""}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Panel>

        {!data?.tracker ? (
          <Panel>
            <Typography sx={{ color: CW.muted }}>No journey with a repeating check-in is assigned to you.</Typography>
          </Panel>
        ) : (
          <>
            <InfoHint id="planner.days" sx={{ ml: 0, mb: 1 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: `repeat(${(data.days || []).length || 5}, 1fr)` },
                gap: 2,
              }}
            >
              {(data.days || []).map((day) => {
                const on = dayFilter === day.date;
                return (
                  <Box
                    key={day.date}
                    component="button"
                    type="button"
                    onClick={() => setDayFilter(on ? "" : day.date)}
                    title={on ? "Show all clients" : "Show clients opening this day"}
                    sx={{
                      font: "inherit",
                      border: `1.5px solid ${on ? CW.primary : CW.line}`,
                      borderRadius: "14px",
                      bgcolor: on ? CW.tealSoft : "#fff",
                      p: 2.25,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <Typography sx={{ fontSize: 19, fontWeight: 700, color: CW.ink }}>{day.weekday}</Typography>
                    <Eyebrow sx={{ fontSize: 11, mt: 0.25 }}>{day.label}</Eyebrow>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mt: 2 }}>
                      <Typography sx={{ fontSize: 38, fontWeight: 700, color: day.count ? CW.primary : CW.muted, lineHeight: 1 }}>
                        {day.count}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: CW.muted }}>check-ins open</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: CW.muted, mt: 2 }}>Scheduled openings</Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", alignItems: "center", my: 2.5 }}>
              <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: CW.ink }}>
                {data.totals?.openings || 0} scheduled openings · {data.totals?.clients || 0} clients
                {!canChangeDay ? (
                  <Pill tone="muted" sx={{ ml: 1.5 }}>
                    Day change off
                  </Pill>
                ) : null}
              </Typography>
              <Typography sx={{ fontSize: 13, color: CW.muted }}>Actual submissions and replies appear in Today.</Typography>
            </Box>

            <Panel sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                <Typography sx={{ fontSize: { xs: 19, md: 23 }, fontWeight: 700, color: CW.ink }}>
                  {dayFilter ? `Clients opening ${moment(dayFilter).format("dddd D MMM")}` : "Clients in this programme"}
                  <InfoHint id="planner.clients" />
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  {dayFilter ? (
                    <TextLink onClick={() => setDayFilter("")} sx={{ fontSize: 13 }}>
                      Show all
                    </TextLink>
                  ) : null}
                  <TextField
                    size="small"
                    placeholder="Search client"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    inputProps={{ "aria-label": "Search client" }}
                    sx={{ width: 200, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: CW.soft }, "& fieldset": { borderColor: "transparent" } }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: COLS, gap: 2, pb: 1.25 }}>
                {["Client", "Check-in day", "Opens this week", "Planned review", "Action"].map((h) => (
                  <Eyebrow key={h}>{h}</Eyebrow>
                ))}
              </Box>
              {rows.length === 0 ? (
                <Typography sx={{ color: CW.muted, fontSize: 13.5, py: 2, borderTop: `1px solid ${CW.line}` }}>No clients for this view.</Typography>
              ) : (
                rows.map((row) => (
                  <Box
                    key={row.personKey}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr 1fr", md: COLS },
                      gap: { xs: 0.75, md: 2 },
                      alignItems: "center",
                      py: 1.75,
                      borderTop: `1px solid ${CW.line}`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: CW.ink, gridColumn: { xs: "1 / -1", md: "auto" } }} noWrap>
                      {row.name}
                    </Typography>
                    <Typography sx={{ fontSize: 14.5, color: row.changedFrom ? CW.primary : CW.ink }}>
                      {row.changedFrom ? `${row.changedFrom} → ${row.checkInDay}` : row.checkInDay || "—"}
                    </Typography>
                    <Box>
                      {row.skippedThisWeek ? (
                        <Pill tone="amber">Skipped</Pill>
                      ) : (
                        <Typography sx={{ fontSize: 14.5, color: CW.muted }}>{row.opensLabel || "—"}</Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 14.5, color: row.plannedReview ? CW.ink : CW.muted }}>
                      {row.plannedReview || "Not planned"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                      <TextLink onClick={() => openEditor(row, "plan")} sx={{ fontSize: 14 }}>
                        Schedule / skip
                      </TextLink>
                      {canChangeDay ? (
                        <>
                          <Box component="span" sx={{ color: CW.muted }}>·</Box>
                          <TextLink onClick={() => openEditor(row, "day")} sx={{ fontSize: 14 }}>
                            Change day
                          </TextLink>
                        </>
                      ) : null}
                    </Box>
                  </Box>
                ))
              )}
              {changedRow ? (
                <Typography sx={{ fontSize: 12.5, color: CW.muted, pt: 1.5, borderTop: `1px solid ${CW.line}` }}>
                  Old day → new day: the client's own day. Past check-ins kept their dates; the new day starts after one bridging window.
                </Typography>
              ) : null}
            </Panel>
            <Typography sx={{ fontSize: 14, color: CW.muted, mt: 2.5 }}>
              A planned review day helps organise work. The reply deadline still follows the actual submission.
            </Typography>
          </>
        )}
      </Box>

      {editing ? (
        <>
          <ScheduleChangeDialog
            open={editing.mode === "day"}
            onClose={() => setEditing(null)}
            trackerId={data.tracker.id}
            personKey={editing.personKey}
            memberId={editing.row.memberId}
            personName={editing.row.name}
            trackerName={data.tracker.name}
            current={editing.schedule.current}
            rules={editing.schedule.rules}
            onSaved={load}
          />
          <PlanSkipDialog
            open={editing.mode === "plan"}
            initialTab="plan"
            onClose={() => setEditing(null)}
            trackerId={data.tracker.id}
            personKey={editing.personKey}
            memberId={editing.row.memberId}
            personName={editing.row.name}
            schedule={editing.schedule}
            onChangeDay={canChangeDay ? () => setEditing({ ...editing, mode: "day" }) : undefined}
            onSaved={load}
          />
        </>
      ) : null}
    </div>
  );
}
